import type { Match, Slot, Tournament } from "./model";

type Route = { teamId: string; outcomes: Map<string, "winner" | "loser"> };

export function assignEliminationReferees(t: Tournament): boolean {
  const matches = new Map(t.matches.map((m) => [m.id, m]));
  const paths = new Map<string, Route[]>();
  const routes = (slot: Slot): Route[] => {
    if ("bye" in slot) return [];
    if ("teamId" in slot) return [{ teamId: slot.teamId, outcomes: new Map() }];
    const key = `${slot.matchId}:${slot.outcome}`;
    if (paths.has(key)) return paths.get(key)!;
    const source = matches.get(slot.matchId);
    if (!source) return [];
    if (source.status === "complete") {
      const id = slot.outcome === "winner" ? source.winnerId : source.loserId;
      return id ? [{ teamId: id, outcomes: new Map() }] : [];
    }
    const result = source.slots.flatMap(routes).map((route) => ({
      teamId: route.teamId,
      outcomes: new Map([...route.outcomes, [source.id, slot.outcome]]),
    }));
    paths.set(key, result);
    return result;
  };
  const available = (slot: Slot, match: Match) => {
    const candidates = routes(slot);
    const players = match.slots.flatMap(routes);
    return (
      candidates.length > 0 &&
      candidates.every(
        (candidate) =>
          !t.teams.find((team) => team.id === candidate.teamId)?.withdrawn &&
          !players.some(
            (player) =>
              player.teamId === candidate.teamId &&
              [...candidate.outcomes].every(
                ([id, outcome]) =>
                  !player.outcomes.has(id) ||
                  player.outcomes.get(id) === outcome,
              ),
          ),
      )
    );
  };
  const used = new Set(
    t.matches.flatMap((m) =>
      m.refereeSlot && "matchId" in m.refereeSlot
        ? [`${m.refereeSlot.matchId}:${m.refereeSlot.outcome}`]
        : [],
    ),
  );
  const duties = new Map<string, number>();
  for (const m of t.matches)
    if (m.refereeId)
      duties.set(m.refereeId, (duties.get(m.refereeId) ?? 0) + 1);
  let changed = false;
  const ordered = t.matches.toSorted((a, b) => a.wave - b.wave);
  const opening = ordered.filter(
    (m) =>
      m.slots.every((slot) => "teamId" in slot) &&
      !m.poolId &&
      m.stage !== "swiss",
  );
  const simultaneous = Math.max(
    1,
    Math.min(t.courts.length, Math.floor(t.teams.length / 3)),
  );
  const firstGames = opening.slice(0, simultaneous);
  const firstPlayers = new Set(firstGames.flatMap((m) => m.teams));
  for (const [index, match] of ordered.entries()) {
    if (
      match.poolId ||
      match.stage === "swiss" ||
      match.refereeMode !== "auto" ||
      match.status === "live" ||
      match.status === "complete"
    )
      continue;
    const before = JSON.stringify([match.refereeSlot, match.refereeId]);
    if (match.refereeSlot && !available(match.refereeSlot, match)) {
      delete match.refereeSlot;
      delete match.refereeId;
    }
    if (!match.refereeSlot) {
      const earlier = ordered
        .slice(0, index)
        .filter(
          (m) => !m.poolId && m.stage !== "swiss" && m.resolution !== "bye",
        )
        .reverse();
      const openingIndex = opening.indexOf(match);
      const previousOnCourt = opening[openingIndex - simultaneous];
      if (previousOnCourt) earlier.unshift(previousOnCourt);
      if (!firstGames.includes(match)) {
        for (const outcome of ["loser", "winner"] as const) {
          const source = earlier.find(
            (m) =>
              !used.has(`${m.id}:${outcome}`) &&
              available({ matchId: m.id, outcome }, match),
          );
          if (source) {
            match.refereeSlot = { matchId: source.id, outcome };
            used.add(`${source.id}:${outcome}`);
            break;
          }
        }
      }
      if (!match.refereeSlot && openingIndex >= 0) {
        const referee = t.teams
          .filter(
            (team) =>
              (!firstGames.includes(match) || !firstPlayers.has(team.id)) &&
              available({ teamId: team.id }, match),
          )
          .toSorted(
            (a, b) =>
              (duties.get(a.id) ?? 0) - (duties.get(b.id) ?? 0) ||
              a.seed - b.seed,
          )[0];
        if (referee) {
          match.refereeSlot = { teamId: referee.id };
          duties.set(referee.id, (duties.get(referee.id) ?? 0) + 1);
        }
      }
    }
    const slot = match.refereeSlot;
    if (slot && "teamId" in slot) match.refereeId = slot.teamId;
    else if (slot) {
      const source = matches.get(slot.matchId);
      match.refereeId =
        source?.status === "complete"
          ? slot.outcome === "winner"
            ? source.winnerId
            : source.loserId
          : undefined;
    }
    if (before !== JSON.stringify([match.refereeSlot, match.refereeId])) {
      match.revision++;
      changed = true;
    }
  }
  return changed;
}
